import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { CreateDeliverableParams } from "../../../../../../../../../../api/services/client/models";
import { InformationBadge } from "../../../../../../../../../../common/informationBadge/informationBadge";
import {
  FormController,
  MuiBox,
  MuiModal,
  MuiRadioGroup,
  WarningConfirmationModal,
} from "../../../../../../../../../../components";
import {
  convertFormDataToLocalizeObj,
  convertLocalizeObjToFormValue,
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../../../../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ProjectCreateDeliverablesFormKeys,
  ProjectCreateDeliverablesFormProps,
  ProjectExistingDeliverableFilterFormProps,
  ProjectExistingDeliverableFormKeys,
  ProjectExistingDeliverablesType,
} from "../../modelsAndSchemas/models";
import { ProjectAddDeliverableValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { ProjectCreateDeliverableWrapper } from "./ProjectCreateDeliverable.style";

import {
  useAddExistingProjectDeliverableMutation,
  useAddNewProjectDeliverableMutation,
  useGetExistingProjectDeliverables,
  useGetProjectDeliverableById,
  useUpdateProjectDeliverableMutation,
} from "../../../../../../../../../../api/services/project/projectApis";
import {
  ADD_DELIVERABLE_TYPE,
  AddDeliverableType,
} from "../../../../../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../../../../../helpers/constants/siteCoreKeys";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { keyDetailsSelector } from "../../../../../../../../../../redux/slices/project/projectSelector";
import { addDeliverableSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import ProjectExistingDeliverables from "./components/existingDeliverables/ProjectExistingDeliverables";
import ProjectNewDeliverable from "./components/newDeliverable/ProjectNewDeliverable";

interface ProjectCreateClientDeliverableProps {
  visible: boolean;
  handleCloseModal: () => void;
  pageId: string;
  getListOfDeliverablesApi: () => void;
  isEditDeliverable?: boolean;
  deliverableId?: string;
}

export const ProjectCreateClientDeliverable: FC<
  ProjectCreateClientDeliverableProps
> = ({
  visible,
  handleCloseModal,
  pageId,
  getListOfDeliverablesApi,
  isEditDeliverable = false,
  deliverableId,
}) => {
  const dispatch = useDispatch();
  const projectKeyDetails = useSelector(keyDetailsSelector);

  const { id: projectId = "" } = projectKeyDetails || {};
  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );
  const [selectedUnit, setSelectedUnit] = useState<string>("");

  const [showCancelModal, setShowCancelModal] = useState(false);

  const [existingProjectDeliverablesList, setExistingProjectDeliverablesList] =
    useState<ProjectExistingDeliverablesType[]>([]);

  // updated sitecore data
  const [
    allExistingProjectDeliverablesUpdatedRows,
    setAllExistingProjectDeliverablesUpdatedRows,
  ] = useState<ProjectExistingDeliverablesType[]>([]);

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const tableExistingDeliverablesRef = useRef<{ resetPagination: () => void }>(
    null,
  );

  const [measurementShouldReset, setMeasurementShouldReset] = useState(true);

  const {
    commonLabels,
    fetchLabel,
    commonValidationMessages,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    data: existingProjectDeliverablesData,
    isLoading: existingProjectDeliverablePending,
    refetchWithParams: refetchExistingProjectDeliverable,
    isError: isExistingProjectDeliverableError,
    isSuccess: isExistingProjectDeliverableSuccess,
  } = useGetExistingProjectDeliverables();

  const {
    mutateAsync: addNewProjectDeliverableApi,
    isPending: isAddNewDeliverablePending,
    isError: isAddNewDeliverableError,
    isSuccess: isAddNewDeliverableSuccess,
  } = useAddNewProjectDeliverableMutation();

  const {
    mutateAsync: addExistingProjectDeliverableApi,
    isPending: isAddExistingDeliverablePending,
    isError: isAddExistingDeliverableError,
    isSuccess: isAddExistingDeliverableSuccess,
  } = useAddExistingProjectDeliverableMutation();

  const {
    mutateAsync: updateProjectDeliverableApi,
    isPending: isUpdateProjectDeliverablePending,
    isError: isUpdateProjectDeliverableError,
    isSuccess: isUpdateProjectDeliverableSuccess,
  } = useUpdateProjectDeliverableMutation();

  const {
    data: projectDeliverableData,
    isPending: isPendingProjectDeliverable,
    isError: isErrorProjectDeliverable,
    isSuccess: isSuccessProjectDeliverable,
    refetchWithParams: getProjectDeliverableData,
  } = useGetProjectDeliverableById();

  useEffect(() => {
    if (isEditDeliverable && deliverableId) {
      getProjectDeliverableData(deliverableId, projectId);
    }
  }, [isEditDeliverable]);

  useEffect(() => {
    if (isExistingProjectDeliverableSuccess) {
      resetExistingProjectDeliverables();
    }
  }, [existingProjectDeliverablesData]);

  const resetExistingProjectDeliverables = () => {
    if (existingProjectDeliverablesData) {
      const myData = [...existingProjectDeliverablesData];

      myData.forEach((data) => {
        data.unitName = fetchValueByKeyOfDropdown(
          addDeliverableSiteCoreKeys.addDeliverable,
          addDeliverableSiteCoreKeys.unitsList,
          data.units,
        );
        data.deliverableTypeName = fetchValueByKeyOfDropdown(
          addDeliverableSiteCoreKeys.addDeliverable,
          addDeliverableSiteCoreKeys.deliverableTypeList,
          data.deliverableType,
        );
        data.measurements =
          data.units === UnitTypesKey.Minutes
            ? Number(
                fetchValueByKeyOfDropdown(
                  addDeliverableSiteCoreKeys.addDeliverable,
                  addDeliverableSiteCoreKeys.minutesList,
                  data.measurements,
                ),
              )
            : Number(data.measurements);
      });
      setExistingProjectDeliverablesList([...myData]);
      setAllExistingProjectDeliverablesUpdatedRows([...myData]);
      // Check if all rows have isSelected as true
      const allSelected = myData.every((row) => row.isSelected);
      setIsAllSelected(allSelected);

      resetExistingFilter();
      resetPagination();
    }
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isAddNewDeliverablePending ||
          isAddExistingDeliverablePending ||
          isPendingProjectDeliverable ||
          isUpdateProjectDeliverablePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isAddNewDeliverablePending,
    isAddExistingDeliverablePending,
    isPendingProjectDeliverable,
    isUpdateProjectDeliverablePending,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: existingProjectDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [existingProjectDeliverablePending]);

  useEffect(() => {
    if (
      isAddExistingDeliverableSuccess ||
      isAddNewDeliverableSuccess ||
      isUpdateProjectDeliverableSuccess
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            addDeliverableSiteCoreKeys.addDeliverable,
            isEditDeliverable && isUpdateProjectDeliverableSuccess
              ? addDeliverableSiteCoreKeys.deliverableUpdatedMessage
              : addDeliverableSiteCoreKeys.deliverableAddedMessage,
          ),
        }),
      );
      getListOfDeliverablesApi();
      handleCloseModal();
    }
    if (
      isAddNewDeliverableError ||
      isAddExistingDeliverableError ||
      isExistingProjectDeliverableError ||
      isErrorProjectDeliverable ||
      isUpdateProjectDeliverableError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isAddNewDeliverableSuccess,
    isAddNewDeliverableError,
    isAddExistingDeliverableError,
    isAddExistingDeliverableSuccess,
    isExistingProjectDeliverableError,
    isErrorProjectDeliverable,
    isUpdateProjectDeliverableError,
    isUpdateProjectDeliverableSuccess,
  ]);

  const defaultValues = {
    [ProjectCreateDeliverablesFormKeys.addDeliverableType]:
      ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType,
    [ProjectCreateDeliverablesFormKeys.deliverableType]: "",
    [ProjectCreateDeliverablesFormKeys.name]: { [userSelectedLanguage]: "" },
    [ProjectCreateDeliverablesFormKeys.units]: "",
    [ProjectCreateDeliverablesFormKeys.measurementMinute]: "",
    [ProjectCreateDeliverablesFormKeys.measurementOther]: null,
  };

  const {
    handleSubmit: handleSubmitForm,
    control,
    getValues,
    watch,
    setValue,
    formState: { isDirty: isDirtyForm },
    reset,
    clearErrors,
  } = useForm<ProjectCreateDeliverablesFormProps>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      ProjectAddDeliverableValidationSchema(
        commonValidationMessages,
        userSelectedLanguage,
      ) as any,
    ),
  });

  useEffect(() => {
    if (isSuccessProjectDeliverable && projectDeliverableData) {
      setSelectedUnit(projectDeliverableData.units);
      reset({
        ...defaultValues,
        [ProjectCreateDeliverablesFormKeys.name]: convertLocalizeObjToFormValue(
          projectDeliverableData.localizedDeliverableNames,
        ),
        [ProjectCreateDeliverablesFormKeys.deliverableType]:
          projectDeliverableData.deliverableType,
        [ProjectCreateDeliverablesFormKeys.units]: projectDeliverableData.units,
        ...(projectDeliverableData.units === UnitTypesKey.Minutes
          ? {
              [ProjectCreateDeliverablesFormKeys.measurementMinute]:
                projectDeliverableData.measurement,
            }
          : {
              [ProjectCreateDeliverablesFormKeys.measurementOther]:
                projectDeliverableData.measurement,
            }),
      });
      setMeasurementShouldReset(false);
    }
  }, [isSuccessProjectDeliverable, projectDeliverableData]);

  useEffect(() => {
    if (getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
      // need to check update scenario
      reset({
        ...defaultValues,
        [ProjectCreateDeliverablesFormKeys.addDeliverableType]:
          getValues().addDeliverableType,
      });
      setSelectedUnit("");
    } else {
      resetExistingProjectDeliverables();
    }
  }, [watch(ProjectCreateDeliverablesFormKeys.addDeliverableType)]);

  useEffect(() => {
    if (measurementShouldReset) {
      clearErrors([
        ProjectCreateDeliverablesFormKeys.measurementMinute,
        ProjectCreateDeliverablesFormKeys.measurementOther,
      ]);
      setValue(ProjectCreateDeliverablesFormKeys.measurementMinute, "");
      setValue(ProjectCreateDeliverablesFormKeys.measurementOther, null);
    }
    setMeasurementShouldReset(true);
  }, [watch(ProjectCreateDeliverablesFormKeys.units)]);

  const onFinalSubmitClick = () => {
    if (getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
      const {
        name,
        deliverableType,
        units,
        measurementMinute,
        measurementOther,
      } = getValues();

      const getMeasurement = () => {
        switch (units) {
          case UnitTypesKey.Minutes:
            return measurementMinute;
          case UnitTypesKey.Count:
          case UnitTypesKey.Days:
            return (measurementOther as number).toString();
          default:
            return "1";
        }
      };

      const params: CreateDeliverableParams = {
        deliverableType,
        units,
        measurements: getMeasurement(),
        localizedDeliverableNames: convertFormDataToLocalizeObj(name),
      };

      if (isEditDeliverable) {
        if (deliverableId) {
          updateProjectDeliverableApi({ params, deliverableId, projectId });
        }
      } else {
        addNewProjectDeliverableApi({ params, projectId });
      }
    } else {
      //existingKey
      const selectedDeliverablesIds: string[] = existingProjectDeliverablesList
        .filter((row) => row.isSelected)
        .map((item) => item.id);

      if (isNotEmpty(selectedDeliverablesIds)) {
        const params = {
          deliverableIds: selectedDeliverablesIds,
        };
        addExistingProjectDeliverableApi({
          params,
          projectId,
        });
      }
    }
  };

  const onConfirmClick = () => {
    handleSubmitForm(onFinalSubmitClick)();
  };

  const closeModalHandler = () => setShowCancelModal(false);

  const cancelMainModalHandler = () => {
    if (isDirtyForm) setShowCancelModal(true);
    else handleCloseModal();
  };

  const cancelConfirmHandler = () => {
    setShowCancelModal(false);
    handleCloseModal();
  };

  const deliverablesTitle = useMemo(() => {
    if (isEditDeliverable) {
      return replaceHashWithValue(
        fetchLabel(
          addDeliverableSiteCoreKeys.addDeliverable,
          addDeliverableSiteCoreKeys.editDeliverableHeading,
        ),
        getValueOrBlank(projectDeliverableData?.name),
      );
    } else {
      return fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.addDeliverableHeading,
      );
    }
  }, [projectDeliverableData]);

  const radioOptions = [
    {
      value: ADD_DELIVERABLE_TYPE.newKey,
      label: fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.addNewDeliverableRadioButton,
      ),
    },
    {
      value: ADD_DELIVERABLE_TYPE.existingKey,
      label: fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.selectExistingDeliverablesRadioButton,
      ),
    },
  ];

  const onSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      ProjectCreateDeliverablesFormKeys.addDeliverableType,
      e.target.value as AddDeliverableType,
    );

    if (!isExistingProjectDeliverableSuccess) {
      refetchExistingProjectDeliverable();
    }
  };

  const onUnitValueChange = (value: string) => {
    setSelectedUnit(value);
  };

  const onCheckAllChange = (checked: boolean) => {
    setIsAllSelected(checked);
    const updatedRows = existingProjectDeliverablesList.map((row) => ({
      ...row,
      isSelected: checked,
    }));
    setExistingProjectDeliverablesList(updatedRows);

    // Also update `allUpdatedRows` to reflect changes for the whole dataset
    const updatedAllRows = allExistingProjectDeliverablesUpdatedRows.map(
      (row) => {
        // Find the matching row in `existingClientDeliverablesList` and update it
        const matchingRow = updatedRows.find(
          (updatedRow) => updatedRow.id === row.id,
        );
        return matchingRow
          ? { ...row, isSelected: matchingRow.isSelected }
          : row;
      },
    );

    // Update `allUpdatedRows` state
    setAllExistingProjectDeliverablesUpdatedRows(updatedAllRows);
  };

  const handleRowCheckboxChange = (checked: boolean, rowId: string) => {
    // Update only the clicked row's `isSelected` value
    const updatedRows = existingProjectDeliverablesList.map((row) =>
      row.id === rowId ? { ...row, isSelected: checked } : row,
    );
    setExistingProjectDeliverablesList(updatedRows);

    // Check if all rows have isSelected as true
    const allSelected = updatedRows.every((row) => row.isSelected);
    setIsAllSelected(allSelected);

    const updatedAllRows = allExistingProjectDeliverablesUpdatedRows.map(
      (row) => (row.id === rowId ? { ...row, isSelected: checked } : row),
    );
    setAllExistingProjectDeliverablesUpdatedRows(updatedAllRows);
  };

  const {
    control: existingFilterFormControl,
    watch: existingWatch,
    reset: resetExistingFilter,
    getValues: getExistingFilterValues,
  } = useForm<ProjectExistingDeliverableFilterFormProps>({
    mode: "all",
    defaultValues: {
      [ProjectExistingDeliverableFormKeys.name]: "",
      [ProjectExistingDeliverableFormKeys.deliverableType]: "",
      [ProjectExistingDeliverableFormKeys.units]: "",
    },
  });

  const applyFilters = (data: ProjectExistingDeliverablesType[]) => {
    const filters = getExistingFilterValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue?.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        if (filterValue === "") return true;

        const itemValue = item[key]?.toString().toLowerCase();

        return itemValue?.includes(filterValueStr);
      });
    });
  };

  useEffect(() => {
    if (isNotEmpty(allExistingProjectDeliverablesUpdatedRows)) {
      const filteredData = applyFilters(
        allExistingProjectDeliverablesUpdatedRows,
      );
      setExistingProjectDeliverablesList(filteredData);
      resetPagination();
    }
  }, [
    existingWatch(ProjectExistingDeliverableFormKeys.name),
    existingWatch(ProjectExistingDeliverableFormKeys.deliverableType),
    existingWatch(ProjectExistingDeliverableFormKeys.units),
  ]);

  const resetPagination = () => {
    if (tableExistingDeliverablesRef.current) {
      tableExistingDeliverablesRef.current.resetPagination();
    }
  };

  const activityHeaderText = useMemo(() => {
    if (isEditDeliverable) {
      return fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        projectDeliverableData?.isProjectFrameworkDeliverable
          ? addDeliverableSiteCoreKeys.thisIsAFrameworkLevelDeliverable
          : addDeliverableSiteCoreKeys.thisIsAProjectSpecificDeliverable,
      );
    }
    return fetchLabel(
      addDeliverableSiteCoreKeys.addDeliverable,
      addDeliverableSiteCoreKeys.projectSpecificDeliverableNote,
    );
  }, [projectDeliverableData]);

  return (
    <MuiModal
      open={visible}
      onClose={cancelMainModalHandler}
      title={deliverablesTitle}
      primaryButtonTitle={
        isEditDeliverable ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={cancelMainModalHandler}
      hasLargeContent
    >
      <ProjectCreateDeliverableWrapper
        data-testid={`${pageId}-add-deliverable`}
      >
        <InformationBadge message={activityHeaderText} />
        {!isEditDeliverable && (
          <MuiBox className="radio-container">
            <FormController
              control={control}
              name={ProjectCreateDeliverablesFormKeys.addDeliverableType}
            >
              <MuiRadioGroup
                label={fetchLabel(
                  addDeliverableSiteCoreKeys.addDeliverable,
                  addDeliverableSiteCoreKeys.selectType,
                )}
                value={
                  getValues(
                    ProjectCreateDeliverablesFormKeys.addDeliverableType,
                  ) ?? ""
                }
                onChange={onSearchTypeChange}
                options={radioOptions}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
        )}

        {getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey && (
          <ProjectNewDeliverable
            pageId={pageId}
            control={control}
            selectedUnit={selectedUnit}
            manageLocaleTitle={deliverablesTitle}
            manageLocaleInfoMessage={activityHeaderText}
            onClickSaveManageLocal={(manageLocals) =>
              setValue(ProjectCreateDeliverablesFormKeys.name, manageLocals)
            }
            onUnitValueChange={onUnitValueChange}
            userSelectedLanguage={userSelectedLanguage}
          />
        )}

        {getValues().addDeliverableType ===
          ADD_DELIVERABLE_TYPE.existingKey && (
          <ProjectExistingDeliverables
            onCheckAllChange={onCheckAllChange}
            isAllChecked={isAllSelected}
            pageId={pageId}
            handleRowCheckboxChange={handleRowCheckboxChange}
            existingFilterFormControl={existingFilterFormControl}
            tableRef={tableExistingDeliverablesRef}
            existingDeliverablesList={existingProjectDeliverablesList}
            noDataInApi={allExistingProjectDeliverablesUpdatedRows.length === 0}
          />
        )}

        {showCancelModal && (
          <WarningConfirmationModal
            show={showCancelModal}
            modalTitle={commonLabels.cancelChanges}
            secondaryButtonTitle={commonLabels.no}
            onClose={closeModalHandler}
            onPrimaryButtonClick={cancelConfirmHandler}
            labelContent={commonLabels.cancelButtonAlertText}
          />
        )}
      </ProjectCreateDeliverableWrapper>
    </MuiModal>
  );
};
